import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { chatBotService } from '../../core/services/chatBotService';

interface ChatSession {
    session_id: string;
    title: string;
    message_count: number;
    created_at: string;
    updated_at: string;
}

interface ChatHistoryProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    tenantId: string;
    currentSessionId: string | null;
    onSessionSelect: (sessionId: string) => void;
    onNewChat: () => void;
    isFullscreen: boolean;
    refreshTrigger?: number; // Add refresh trigger prop
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
    isOpen,
    onClose,
    userId,
    tenantId,
    currentSessionId,
    onSessionSelect,
    onNewChat,
    isFullscreen,
    refreshTrigger
}) => {
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [allSessions, setAllSessions] = useState<ChatSession[]>([]);
    const [filteredSessions, setFilteredSessions] = useState<ChatSession[]>([]);
    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && userId && tenantId) {
            loadChatSessions();
        }
    }, [isOpen, userId, tenantId]);

    // Refresh sessions when refreshTrigger changes (silent refresh - no loading state)
    useEffect(() => {
        if (refreshTrigger && userId && tenantId) {
            loadChatSessions(true); // Silent refresh
        }
    }, [refreshTrigger, userId, tenantId]);

    const loadChatSessions = async (silent = false) => {
        if (!silent) {
            setIsLoading(true);
        }
        try {
            const chatSessions = await chatBotService.getUserChatSessions(userId, tenantId);
            setAllSessions(chatSessions);
            setFilteredSessions(chatSessions);
            setSessions(chatSessions);
        } catch (error) {
            console.error('Error loading chat sessions:', error);
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    };

    const handleSearch = (query: string) => {
        if (!query.trim() || query.length < 1) {
            setSessions(allSessions);
            return;
        }

        const filtered = allSessions.filter(session => 
            session.title.toLowerCase().includes(query.toLowerCase())
        );
        setSessions(filtered);
    };

    useEffect(() => {
        handleSearch(searchQuery);
    }, [searchQuery, allSessions]);

    const handleNewChat = () => {
        onNewChat();
        if (!isFullscreen) {
            onClose();
        }
    };

    const handleRename = async (sessionId: string, newTitle: string) => {
        try {
            await chatBotService.renameChatSession(sessionId, newTitle, userId, tenantId);
            setSessions(prev => prev.map(session => 
                session.session_id === sessionId 
                    ? { ...session, title: newTitle }
                    : session
            ));
            setEditingSessionId(null);
            setEditTitle('');
        } catch (error) {
            console.error('Error renaming chat session:', error);
        }
    };

    const handleDelete = async (sessionId: string) => {
        try {
            await chatBotService.deleteChatSession(sessionId, userId, tenantId);
            setSessions(prev => prev.filter(session => session.session_id !== sessionId));
            setDeleteSessionId(null);
            
            // If deleted session was current, trigger new chat
            if (sessionId === currentSessionId) {
                onNewChat();
            }
        } catch (error) {
            console.error('Error deleting chat session:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return 'Today';
        if (diffDays === 2) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        return date.toLocaleDateString();
    };

    const groupSessionsByDate = useCallback((sessions: ChatSession[]) => {
        const groups: { [key: string]: ChatSession[] } = {};
        
        sessions.forEach(session => {
            const dateKey = formatDate(session.updated_at);
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(session);
        });
        
        return groups;
    }, []);
    
    const groupedSessions = useMemo(() => {
        const grouped = groupSessionsByDate(sessions);
        return grouped;
    }, [sessions, groupSessionsByDate]);

    if (!isOpen) return null;

    return (
        <div className={`chatbot-history-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
            <div className={`chatbot-history-sidebar ${isFullscreen ? 'fullscreen' : ''}`}>
                {/* Header */}
                {!isFullscreen && (
                    <div className="chatbot-history-header">
                        <div className="chatbot-history-title">
                            <i className="bi bi-clock-history"></i>
                            <span>Chat History</span>
                        </div>
                        <button className="chatbot-history-close" onClick={onClose}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                 )}

                {/* Actions */}
                <div className="chatbot-history-actions">
                    <button className="chatbot-new-chat-btn" onClick={handleNewChat}>
                        <i className="bi bi-plus-circle"></i>
                        <span>New Chat</span>
                    </button>
                    
                    <div className="chatbot-search-container">
                        <input
                            type="text"
                            placeholder="Search chats..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="chatbot-search-input"
                        />
                        <div className="chatbot-search-icon">
                            <i className="bi bi-search"></i>
                        </div>
                    </div>
                </div>

                {/* Sessions List */}
                <div className="chatbot-sessions-list">
                    {isLoading ? (
                        <div className="chatbot-loading-state">
                            <div className="chatbot-loading-spinner"></div>
                            <span>Loading chats...</span>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="chatbot-empty-state">
                            <i className="bi bi-chat-dots"></i>
                            <span>No chats found</span>
                        </div>
                    ) : (
                        Object.entries(groupedSessions).map(([dateGroup, groupSessions]) => (
                                <div key={dateGroup} className="chatbot-date-group">
                                    <div className="chatbot-date-group-header">{dateGroup}</div>
                                {groupSessions.map((session) => (
                                    <div
                                        key={session.session_id}
                                        className={`chatbot-session-item ${session.session_id === currentSessionId ? 'active' : ''}`}
                                        onClick={() => {
                                            onSessionSelect(session.session_id);
                                            if (!isFullscreen) {
                                                onClose();
                                            }
                                        }}
                                    >
                                            <div className="chatbot-session-content">
                                            {editingSessionId === session.session_id ? (
                                                <input
                                                    type="text"
                                                    value={editTitle}
                                                    onChange={(e) => setEditTitle(e.target.value)}
                                                    onBlur={() => handleRename(session.session_id, editTitle)}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleRename(session.session_id, editTitle);
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setEditingSessionId(null);
                                                            setEditTitle('');
                                                        }
                                                    }}
                                                    className="chatbot-edit-title-input"
                                                    autoFocus
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <div className="chatbot-session-title">{session.title}</div>
                                            )}
                                        </div>
                                        
                                            <div className="chatbot-session-actions">
                                            <button
                                                className="chatbot-action-btn rename-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingSessionId(session.session_id);
                                                    setEditTitle(session.title);
                                                }}
                                                title="Rename"
                                            >
                                                <i className="bi bi-pencil"></i>
                                            </button>
                                            <button
                                                className="chatbot-action-btn delete-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteSessionId(session.session_id);
                                                }}
                                                title="Delete"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            {deleteSessionId && (
                isFullscreen ? (
                    createPortal(
                        <div className="chatbot-delete-modal-overlay" onClick={() => setDeleteSessionId(null)}>
                            <div className="chatbot-delete-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="chatbot-delete-modal-header">
                                    <h3>Delete Chat</h3>
                                    <button className="chatbot-modal-close" onClick={() => setDeleteSessionId(null)}>
                                        <i className="bi bi-x"></i>
                                    </button>
                                </div>
                                <div className="chatbot-delete-modal-body">
                                    <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
                                </div>
                                <div className="chatbot-delete-modal-actions">
                                    <button className="chatbot-cancel-btn" onClick={() => setDeleteSessionId(null)}>
                                        Cancel
                                    </button>
                                    <button className="chatbot-delete-confirm-btn" onClick={() => handleDelete(deleteSessionId)}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )
                ) : (
                        <div className="chatbot-delete-modal-overlay" onClick={() => setDeleteSessionId(null)}>
                            <div className="chatbot-delete-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="chatbot-delete-modal-header">
                                <h3>Delete Chat</h3>
                                    <button className="chatbot-modal-close" onClick={() => setDeleteSessionId(null)}>
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                                <div className="chatbot-delete-modal-body">
                                <p>Are you sure you want to delete this chat? This action cannot be undone.</p>
                            </div>
                                <div className="chatbot-delete-modal-actions">
                                    <button className="chatbot-cancel-btn" onClick={() => setDeleteSessionId(null)}>
                                    Cancel
                                </button>
                                    <button className="chatbot-delete-confirm-btn" onClick={() => handleDelete(deleteSessionId)}>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )
            )}
        </div>
    );
};

export default ChatHistory;